import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, CheckCircle2, Clock, StickyNote, X, Plus,
  Sparkles, BookOpen, Trash2, ChevronRight,
  Volume2, Pause, Play, Square, Highlighter, MessageSquare, Bot, Send, User,
  Brain, Download
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LessonReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [ideas, setIdeas] = useState(null);
  const [showIdeas, setShowIdeas] = useState(false);
  const [showNotePanel, setShowNotePanel] = useState(false);
  const [showBotPanel, setShowBotPanel] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [readProgress, setReadProgress] = useState(0);
  const contentRef = useRef(null);

  // Text Highlight
  const [selectionPopover, setSelectionPopover] = useState(null);

  // Mini BehaviorBot
  const [botMessages, setBotMessages] = useState([]);
  const [botInput, setBotInput] = useState("");
  const [botStreaming, setBotStreaming] = useState(false);
  const [botCurrentResponse, setBotCurrentResponse] = useState("");
  const botBottomRef = useRef(null);

  // Quiz
  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);

  // TTS
  const [ttsActive, setTtsActive] = useState(false);
  const [ttsPaused, setTtsPaused] = useState(false);
  const [ttsSpeed, setTtsSpeed] = useState(1);
  const utteranceRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/lessons/${id}`),
      axios.get(`${API}/notes/lesson/${id}`)
    ]).then(([lr, nr]) => {
      setLesson(lr.data);
      setNotes(nr.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    return () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); };
  }, []);

  useEffect(() => {
    const h = () => {
      const el = document.documentElement;
      const st = el.scrollTop || document.body.scrollTop;
      const sh = el.scrollHeight - el.clientHeight;
      setReadProgress(sh > 0 ? Math.min((st / sh) * 100, 100) : 0);
    };
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    botBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [botMessages, botCurrentResponse]);

  const handleMouseUp = useCallback((e) => {
    if (e.target.closest('[data-testid="highlight-popover"]')) return;
    const sel = window.getSelection();
    if (!sel || sel.toString().trim().length < 5) { setSelectionPopover(null); return; }
    const text = sel.toString().trim();
    if (text.length > 600) { setSelectionPopover(null); return; }
    try {
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (rect.width === 0) { setSelectionPopover(null); return; }
      setSelectionPopover({ text, x: rect.left + rect.width / 2, y: rect.top - 10 });
    } catch (_) { setSelectionPopover(null); }
  }, []);

  const saveHighlight = async () => {
    if (!selectionPopover?.text) return;
    try {
      const res = await axios.post(`${API}/notes`, {
        lesson_id: id, content: selectionPopover.text,
        highlighted_text: selectionPopover.text, note_type: "highlight"
      });
      setNotes(prev => [res.data, ...prev]);
      setSelectionPopover(null);
      window.getSelection().removeAllRanges();
      if (!showNotePanel) setShowNotePanel(true);
    } catch (err) { console.error(err); }
  };

  const buildLessonText = useCallback(() => {
    if (!lesson) return "";
    const parts = [lesson.title + "."];
    if (lesson.intro) parts.push(lesson.intro);
    (lesson.sections || []).forEach(s => {
      if (s.type === "section_title") parts.push(s.text + ":");
      else if (s.text) parts.push(s.text);
    });
    if (lesson.key_takeaways?.length) {
      parts.push("Poin penting:");
      lesson.key_takeaways.forEach((t, i) => parts.push(`${i+1}. ${t}`));
    }
    return parts.join(" ");
  }, [lesson]);

  const handleTTS = () => {
    if (!window.speechSynthesis) { alert("Browser kamu tidak mendukung TTS."); return; }
    if (ttsPaused) { window.speechSynthesis.resume(); setTtsPaused(false); return; }
    if (ttsActive && !ttsPaused) { window.speechSynthesis.pause(); setTtsPaused(true); return; }
    window.speechSynthesis.cancel();
    const text = buildLessonText();
    const startSpeak = () => {
      const u = new SpeechSynthesisUtterance(text);
      const vs = window.speechSynthesis.getVoices();
      const v = vs.find(v => v.lang.startsWith("id")) || vs.find(v => v.lang.startsWith("en")) || vs[0];
      if (v) u.voice = v;
      u.lang = "id-ID"; u.rate = ttsSpeed; u.pitch = 1;
      u.onstart = () => { setTtsActive(true); setTtsPaused(false); };
      u.onend = () => { setTtsActive(false); setTtsPaused(false); };
      u.onerror = () => { setTtsActive(false); setTtsPaused(false); };
      u.onpause = () => setTtsPaused(true);
      u.onresume = () => setTtsPaused(false);
      utteranceRef.current = u;
      window.speechSynthesis.speak(u);
      setTtsActive(true); setTtsPaused(false);
    };
    const vs = window.speechSynthesis.getVoices();
    if (vs.length > 0) startSpeak();
    else {
      window.speechSynthesis.onvoiceschanged = () => { window.speechSynthesis.onvoiceschanged = null; startSpeak(); };
      window.speechSynthesis.speak(new SpeechSynthesisUtterance("")); window.speechSynthesis.cancel();
      setTimeout(startSpeak, 200);
    }
  };

  const handleStopTTS = () => { window.speechSynthesis.cancel(); setTtsActive(false); setTtsPaused(false); };

  const handleSpeedChange = (speed) => {
    setTtsSpeed(speed);
    if (ttsActive) {
      const wasPlaying = !ttsPaused;
      window.speechSynthesis.cancel();
      if (wasPlaying) {
        const u = new SpeechSynthesisUtterance(buildLessonText());
        const vs = window.speechSynthesis.getVoices();
        const v = vs.find(v => v.lang.startsWith("id")) || vs[0];
        if (v) u.voice = v;
        u.lang = "id-ID"; u.rate = speed; u.pitch = 1;
        u.onstart = () => { setTtsActive(true); setTtsPaused(false); };
        u.onend = () => { setTtsActive(false); setTtsPaused(false); };
        u.onerror = () => { setTtsActive(false); setTtsPaused(false); };
        utteranceRef.current = u;
        window.speechSynthesis.speak(u);
        setTtsActive(true); setTtsPaused(false);
      } else { setTtsActive(false); setTtsPaused(false); }
    }
  };

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      const res = await axios.post(`${API}/progress/complete/${id}`);
      setIdeas(res.data.ideas);
      setShowIdeas(true);
      setLesson(prev => ({ ...prev, completed: true }));
    } catch (err) { console.error(err); }
    finally { setCompleting(false); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await axios.post(`${API}/notes`, { lesson_id: id, content: newNote, note_type: "note" });
      setNotes(prev => [res.data, ...prev]);
      setNewNote("");
    } catch (err) { console.error(err); }
  };

  const deleteNote = async (noteId) => {
    await axios.delete(`${API}/notes/${noteId}`);
    setNotes(prev => prev.filter(n => n.id !== noteId));
  };

  const sendBotMessage = async (text) => {
    const msg = (text || botInput).trim();
    if (!msg || botStreaming) return;
    setBotInput("");
    setBotMessages(prev => [...prev, { role: "user", content: msg, id: Date.now() }]);
    setBotStreaming(true);
    setBotCurrentResponse("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, lesson_id: id })
      });
      if (!res.ok) throw new Error("Network error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const lines = decoder.decode(value, { stream: true }).split("\n").filter(l => l.startsWith("data: "));
        for (const line of lines) {
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === "delta") { full += d.content; setBotCurrentResponse(full); }
          } catch (_) {}
        }
      }
      setBotMessages(prev => [...prev, { role: "assistant", content: full, id: Date.now() + 1 }]);
    } catch (err) {
      setBotMessages(prev => [...prev, { role: "assistant", content: "Terjadi kesalahan. Coba lagi.", id: Date.now() + 1 }]);
    } finally { setBotStreaming(false); setBotCurrentResponse(""); }
  };

  // Quiz functions
  const loadQuiz = async () => {
    setShowQuiz(true);
    setQuizResult(null);
    setUserAnswers({});
    if (quiz) return;
    setQuizLoading(true);
    try {
      const res = await axios.get(`${API}/quiz/${id}`);
      setQuiz(res.data);
    } catch (err) { console.error(err); }
    finally { setQuizLoading(false); }
  };

  const submitQuiz = async () => {
    const total = quiz?.questions?.length || 0;
    if (Object.keys(userAnswers).length < total || quizSubmitting) return;
    setQuizSubmitting(true);
    try {
      const answers = quiz.questions.map((_, i) => userAnswers[i] || "");
      const res = await axios.post(`${API}/quiz/${id}/submit`, { lesson_id: id, answers });
      setQuizResult(res.data);
    } catch (err) { console.error(err); }
    finally { setQuizSubmitting(false); }
  };

  const closeQuiz = () => { setShowQuiz(false); setQuizResult(null); setUserAnswers({}); };

  // Export notes for this lesson
  const exportNotes = () => {
    if (!notes.length) return;
    const highlights = notes.filter(n => n.note_type === "highlight");
    const regular = notes.filter(n => n.note_type !== "highlight");
    let md = `# Notes: ${lesson.title}\n`;
    md += `> Diekspor dari BehaviorBase pada ${new Date().toLocaleDateString("id-ID")}\n\n`;
    if (highlights.length > 0) {
      md += `## Highlights\n\n`;
      highlights.forEach(h => { md += `> ${h.content}\n\n`; });
    }
    if (regular.length > 0) {
      md += `## Catatan\n\n`;
      regular.forEach(n => { md += `**${new Date(n.created_at).toLocaleDateString("id-ID")}**\n${n.content}\n\n`; });
    }
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notes-${lesson.title.slice(0, 30).replace(/[^a-z0-9]/gi, "-")}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleNotePanel = () => { setShowNotePanel(p => !p); setShowBotPanel(false); };
  const toggleBotPanel = () => { setShowBotPanel(p => !p); setShowNotePanel(false); };

  if (loading) return <PageLoader />;
  if (!lesson) return <div className="p-8 text-[#94A3B8]">Lesson tidak ditemukan.</div>;

  return (
    <div className="relative" onMouseUp={handleMouseUp}>
      {/* Progress Bar */}
      <div className="fixed top-12 md:top-0 left-0 md:left-64 right-0 h-0.5 bg-white/5 z-50">
        <motion.div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${readProgress}%` }} />
      </div>

      {/* Text Selection Highlight Popover */}
      <AnimatePresence>
        {selectionPopover && (
          <motion.div data-testid="highlight-popover"
            initial={{ opacity: 0, scale: 0.9, y: 4 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 4 }} transition={{ duration: 0.12 }}
            className="fixed z-[60] bg-[#1A1A20] border border-amber-500/30 rounded-xl shadow-2xl flex items-center gap-1 px-2 py-1.5 pointer-events-auto"
            style={{ left: Math.max(8, Math.min(selectionPopover.x - 85, window.innerWidth - 210)), top: Math.max(8, selectionPopover.y - 46) }}>
            <button data-testid="save-highlight-btn"
              onMouseDown={e => { e.preventDefault(); saveHighlight(); }}
              className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 px-2 py-1 rounded-lg hover:bg-amber-500/10 transition-colors duration-150 whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}>
              <Highlighter className="w-3 h-3" />Simpan Highlight
            </button>
            <button onMouseDown={e => { e.preventDefault(); setSelectionPopover(null); window.getSelection().removeAllRanges(); }} className="text-[#4a5568] hover:text-[#94A3B8] p-1">
              <X className="w-3 h-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex">
        {/* Main Content */}
        <main className="flex-1 min-w-0 px-4 md:px-12 py-6 md:py-10 max-w-3xl" ref={contentRef}>
          {/* Back + TTS */}
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <button onClick={() => { handleStopTTS(); navigate("/knowledge"); }} data-testid="back-to-knowledge"
              className="flex items-center gap-2 text-xs text-[#94A3B8] hover:text-white transition-colors duration-200">
              <ArrowLeft className="w-3.5 h-3.5" /><span style={{ fontFamily: "'Inter', sans-serif" }}>Kembali ke Knowledge Base</span>
            </button>
            <button data-testid="tts-listen-btn" onClick={handleTTS}
              className={"flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-xl border transition-all duration-200 " + (
                ttsActive ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300" : "bg-white/5 border-white/15 text-[#94A3B8] hover:text-white hover:border-white/30"
              )} style={{ fontFamily: "'Inter', sans-serif" }}>
              {ttsActive && !ttsPaused ? <><Pause className="w-3.5 h-3.5" />Jeda</>
               : ttsActive && ttsPaused ? <><Play className="w-3.5 h-3.5" />Lanjutkan</>
               : <><Volume2 className="w-3.5 h-3.5" />Dengarkan Lesson</>}
            </button>
          </div>

          {/* Lesson Header */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2 py-1 rounded-full">
                Hari {lesson.day} &middot; Kategori {lesson.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-[#94A3B8]">
                <Clock className="w-3 h-3" />{lesson.duration_minutes} menit
              </span>
              {lesson.completed && (
                <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />Selesai
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-3 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {lesson.title}
            </h1>
            <p className="text-base text-[#94A3B8] leading-relaxed mb-8 border-l-2 border-indigo-500/30 pl-4" style={{ fontFamily: "'IBM Plex Serif', serif" }}>
              {lesson.intro}
            </p>
          </motion.div>

          {/* Highlight Hint */}
          <div className="mb-4 flex items-center gap-2 text-[10px] text-[#4a5568]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            <Highlighter className="w-3 h-3 text-amber-500/40" />// pilih teks untuk menyimpan highlight
          </div>

          {/* Sections */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="space-y-5">
            {(lesson.sections || []).map((s, i) => <SectionBlock key={i} section={s} />)}
          </motion.div>

          {/* Key Takeaways */}
          {lesson.key_takeaways?.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="mt-10 bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-6">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <BookOpen className="w-4 h-4" />Key Takeaways
              </h3>
              <ul className="space-y-2.5">
                {lesson.key_takeaways.map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-indigo-400 text-xs font-mono mt-0.5 flex-shrink-0">{String(i+1).padStart(2,'0')}.</span>
                    <span className="text-sm text-[#F8FAFC] leading-relaxed" style={{ fontFamily: "'IBM Plex Serif', serif" }}>{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Tags */}
          {lesson.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {lesson.tags.map(tag => (
                <span key={tag} className="text-xs font-mono text-[#94A3B8] bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">#{tag}</span>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-10 pt-8 border-t border-white/10">
            {lesson.completed ? (
              <div className="flex items-center gap-3 flex-wrap">
                <button data-testid="view-ideas-btn" onClick={() => setShowIdeas(true)}
                  className="flex items-center gap-2 bg-indigo-500/15 border border-indigo-500/25 text-indigo-300 text-sm px-5 py-2.5 rounded-xl hover:bg-indigo-500/20 transition-colors duration-200"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <Sparkles className="w-4 h-4" />Lihat Ide Konten
                </button>
                <button data-testid="start-quiz-btn" onClick={loadQuiz}
                  className="flex items-center gap-2 bg-purple-500/15 border border-purple-500/25 text-purple-300 text-sm px-5 py-2.5 rounded-xl hover:bg-purple-500/20 transition-colors duration-200"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <Brain className="w-4 h-4" />Mulai Quiz
                </button>
                <span className="text-sm text-emerald-400 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />Lesson selesai
                </span>
              </div>
            ) : (
              <button data-testid="complete-lesson-btn" onClick={handleComplete} disabled={completing}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-colors duration-200"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {completing ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Menghasilkan ide konten...</>
                            : <><CheckCircle2 className="w-4 h-4" />Selesai Baca &amp; Dapatkan Ide Konten</>}
              </button>
            )}
          </div>
          {ttsActive && <div className="h-20" />}
        </main>

        {/* Right Toggle Buttons */}
        <div className="w-12 flex-shrink-0 pt-10 flex flex-col items-center gap-1">
          <button data-testid="toggle-notes-btn" onClick={toggleNotePanel} title="Catatan"
            className={"p-2.5 rounded-xl border transition-colors duration-200 " + (
              showNotePanel ? "bg-indigo-500/15 border-indigo-500/25 text-indigo-400" : "border-white/10 text-[#94A3B8] hover:text-white hover:border-white/20"
            )}>
            <StickyNote className="w-4 h-4" />
          </button>
          {notes.length > 0 && <span className="text-[10px] font-mono text-indigo-400">{notes.length}</span>}
          <div className="mt-3">
            <button data-testid="toggle-bot-btn" onClick={toggleBotPanel} title="Tanya BehaviorBot"
              className={"p-2.5 rounded-xl border transition-colors duration-200 " + (
                showBotPanel ? "bg-indigo-500/15 border-indigo-500/25 text-indigo-400" : "border-white/10 text-[#94A3B8] hover:text-white hover:border-white/20"
              )}>
              <MessageSquare className="w-4 h-4" />
            </button>
            {botMessages.length > 0 && <div className="text-center mt-0.5"><span className="text-[10px] font-mono text-indigo-400">{botMessages.length}</span></div>}
          </div>
        </div>

        {/* Notes Panel */}
        <AnimatePresence>
          {showNotePanel && (
            <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: 280, opacity: 1 }} exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }} className="flex-shrink-0 border-l border-white/10 bg-[#0A0A0C] overflow-hidden">
              <div className="p-5 w-[280px]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Catatan</h3>
                  <div className="flex items-center gap-2">
                    {notes.length > 0 && (
                      <button data-testid="export-notes-lesson-btn" onClick={exportNotes} title="Export ke Markdown"
                        className="text-[#94A3B8] hover:text-indigo-400 transition-colors duration-150">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => setShowNotePanel(false)} className="text-[#94A3B8] hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mb-4">
                  <textarea data-testid="note-input" value={newNote} onChange={e => setNewNote(e.target.value)}
                    placeholder="Tulis catatanmu di sini..." rows={3}
                    className="w-full bg-[#121216] border border-white/10 rounded-xl p-3 text-xs text-white placeholder-[#4a5568] focus:outline-none focus:border-indigo-500/40 resize-none"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) addNote(); }} />
                  <button data-testid="add-note-btn" onClick={addNote}
                    className="mt-2 flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 px-3 py-1.5 rounded-lg transition-colors duration-200"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    <Plus className="w-3 h-3" />Simpan Catatan
                  </button>
                </div>
                <div className="space-y-2">
                  {notes.map(note => (
                    <div key={note.id} data-testid={`note-item-${note.id}`}
                      className={"rounded-xl p-3 group border " + (
                        note.note_type === "highlight" ? "bg-amber-500/8 border-amber-500/25" : "bg-[#121216] border-white/10"
                      )}>
                      {note.note_type === "highlight" && (
                        <span className="text-[9px] font-mono text-amber-400 uppercase tracking-wider mb-1.5 block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Highlight</span>
                      )}
                      <p className="text-xs text-[#F8FAFC] leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{note.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-[#4a5568]">{new Date(note.created_at).toLocaleDateString('id-ID')}</span>
                        <button onClick={() => deleteNote(note.id)} className="text-[#4a5568] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all duration-200">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* BehaviorBot Panel */}
        <AnimatePresence>
          {showBotPanel && (
            <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: 300, opacity: 1 }} exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }} className="flex-shrink-0 border-l border-white/10 bg-[#0A0A0C] overflow-hidden"
              style={{ position: 'sticky', top: '48px', height: 'calc(100vh - 48px)', alignSelf: 'flex-start' }}>
              <div className="w-[300px] flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-indigo-500/15 border border-indigo-500/20 flex items-center justify-center">
                      <Bot className="w-3.5 h-3.5 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>BehaviorBot</h3>
                      <p className="text-[9px] text-indigo-400 truncate max-w-[160px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        // {lesson.title?.slice(0,22)}{lesson.title?.length > 22 ? '...' : ''}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setShowBotPanel(false)} className="text-[#94A3B8] hover:text-white flex-shrink-0"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {botMessages.length === 0 && !botStreaming && (
                    <div className="py-4">
                      <p className="text-[11px] text-[#94A3B8] leading-relaxed mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Tanya apapun tentang lesson ini. BehaviorBot tahu konteks yang sedang kamu baca.
                      </p>
                      {["Jelaskan konsep utama lesson ini", "Cara menerapkan di konten?", "Contoh nyata dari Indonesia?"].map((s, i) => (
                        <button key={i} onClick={() => sendBotMessage(s)}
                          className="w-full text-left text-[10px] text-[#94A3B8] hover:text-white bg-white/3 hover:bg-white/5 border border-white/8 hover:border-white/15 rounded-lg p-2 mb-1.5 transition-colors duration-150 leading-relaxed"
                          style={{ fontFamily: "'Inter', sans-serif" }}>{s}</button>
                      ))}
                    </div>
                  )}
                  {botMessages.map((msg, i) => (
                    <div key={msg.id || i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                      <div className={`w-5 h-5 rounded-md flex-shrink-0 flex items-center justify-center mt-0.5 ${
                        msg.role === "user" ? "bg-indigo-500/20 border border-indigo-500/25" : "bg-white/5 border border-white/10"
                      }`}>
                        {msg.role === "user" ? <User className="w-2.5 h-2.5 text-indigo-400" /> : <Bot className="w-2.5 h-2.5 text-[#94A3B8]" />}
                      </div>
                      <div className={`flex-1 ${msg.role === "user" ? "flex justify-end" : ""}`}>
                        {msg.role === "user" ? (
                          <div className="bg-indigo-600/15 border border-indigo-500/20 rounded-xl rounded-tr-sm px-3 py-2 inline-block max-w-[90%]">
                            <p className="text-[11px] text-white leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{msg.content}</p>
                          </div>
                        ) : (
                          <p className="flex-1 text-[11px] text-[#F8FAFC] leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'IBM Plex Serif', serif" }}>{msg.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {botStreaming && (
                    <div className="flex gap-2">
                      <div className="w-5 h-5 rounded-md flex-shrink-0 flex items-center justify-center mt-0.5 bg-white/5 border border-white/10">
                        <Bot className="w-2.5 h-2.5 text-indigo-400" />
                      </div>
                      <p className="flex-1 text-[11px] text-[#F8FAFC] leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'IBM Plex Serif', serif" }}>
                        {botCurrentResponse}<span className="inline-block w-0.5 h-3 bg-indigo-400 ml-0.5 animate-pulse" />
                      </p>
                    </div>
                  )}
                  <div ref={botBottomRef} />
                </div>
                <div className="p-3 border-t border-white/10 flex-shrink-0">
                  <div className="flex gap-2">
                    <input data-testid="bot-input" value={botInput} onChange={e => setBotInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); sendBotMessage(); } }}
                      placeholder="Tanya tentang lesson ini..." disabled={botStreaming}
                      className="flex-1 bg-[#121216] border border-white/10 rounded-lg px-3 py-2 text-[11px] text-white placeholder-[#4a5568] focus:outline-none focus:border-indigo-500/30 disabled:opacity-50"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                    <button data-testid="bot-send-btn" onClick={() => sendBotMessage()} disabled={!botInput.trim() || botStreaming}
                      className="w-8 h-8 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 rounded-lg flex items-center justify-center transition-colors duration-200 flex-shrink-0">
                      {botStreaming ? <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-3 h-3 text-white" />}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating TTS Player */}
      <AnimatePresence>
        {ttsActive && (
          <motion.div data-testid="tts-player" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2 }} className="fixed bottom-6 z-50" style={{ left: "50%", transform: "translateX(-50%)" }}>
            <div className="bg-[#121216] border border-white/15 rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3 w-[calc(100vw-2rem)] md:w-[380px] max-w-lg">
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {[0,1,2].map(i => (
                  <motion.div key={i} className="w-0.5 bg-indigo-400 rounded-full"
                    animate={ttsPaused ? { height: 8 } : { height: [8, 20, 8] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }} />
                ))}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {ttsPaused ? "Dijeda" : "Membaca..."} {lesson.title}
                </p>
                <p className="text-[10px] text-[#94A3B8] mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Web Speech &middot; {ttsSpeed}x</p>
              </div>
              <button data-testid="tts-play-pause-btn" onClick={handleTTS}
                className="w-8 h-8 flex items-center justify-center bg-indigo-600 hover:bg-indigo-500 rounded-full transition-colors duration-200 flex-shrink-0">
                {ttsPaused ? <Play className="w-3.5 h-3.5 text-white" /> : <Pause className="w-3.5 h-3.5 text-white" />}
              </button>
              <select data-testid="tts-speed-select" value={ttsSpeed} onChange={e => handleSpeedChange(parseFloat(e.target.value))}
                className="text-xs text-[#94A3B8] bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 focus:outline-none hover:border-white/20 cursor-pointer"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                <option value={0.75}>0.75x</option>
                <option value={1}>1x</option>
                <option value={1.25}>1.25x</option>
                <option value={1.5}>1.5x</option>
                <option value={2}>2x</option>
              </select>
              <button data-testid="tts-stop-btn" onClick={handleStopTTS} className="text-[#94A3B8] hover:text-red-400 transition-colors flex-shrink-0">
                <Square className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ideas Modal */}
      <AnimatePresence>
        {showIdeas && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={e => { if (e.target === e.currentTarget) setShowIdeas(false); }}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className="bg-[#121216] border border-white/15 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Ide Konten Untukmu</h2>
                  <p className="text-xs text-[#94A3B8] mt-0.5">Berdasarkan lesson yang baru kamu selesaikan</p>
                </div>
                <button onClick={() => setShowIdeas(false)} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              {ideas && ideas.length > 0 ? (
                <div className="space-y-4">
                  {ideas.map((idea, i) => <IdeaCard key={idea.id || i} idea={idea} index={i} />)}
                  <button onClick={() => { setShowIdeas(false); navigate('/vault'); }}
                    className="w-full flex items-center justify-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 border border-indigo-500/20 rounded-xl py-2.5 mt-2 hover:border-indigo-500/40 transition-colors duration-200"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Lihat di Content Vault <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center py-8 text-[#94A3B8] text-sm">Memuat ide konten...</div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quiz Modal */}
      <AnimatePresence>
        {showQuiz && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6"
            onClick={e => { if (e.target === e.currentTarget) closeQuiz(); }}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className="bg-[#121216] border border-white/15 rounded-2xl max-w-2xl w-full max-h-[88vh] flex flex-col">

              {/* Quiz Header */}
              <div className="flex items-start justify-between p-5 border-b border-white/10 flex-shrink-0">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="w-4 h-4 text-purple-400" />
                    <span className="text-[10px] font-mono text-purple-400 uppercase tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Quiz Lesson</span>
                  </div>
                  <h2 className="text-sm font-bold text-white leading-snug max-w-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{lesson.title}</h2>
                </div>
                <button onClick={closeQuiz} className="text-[#94A3B8] hover:text-white ml-4 flex-shrink-0"><X className="w-5 h-5" /></button>
              </div>

              {/* Quiz Content */}
              <div className="flex-1 overflow-y-auto p-5">
                {quizLoading ? (
                  <div className="flex flex-col items-center py-12">
                    <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mb-3" />
                    <p className="text-xs text-[#94A3B8]" style={{ fontFamily: "'Inter', sans-serif" }}>Generating quiz dari materi lesson...</p>
                  </div>
                ) : quizResult ? (
                  // Results view
                  <div className="space-y-4">
                    <div className={`rounded-2xl p-5 text-center border ${
                      quizResult.score === quizResult.total ? "bg-emerald-500/10 border-emerald-500/20"
                      : quizResult.score >= quizResult.total / 2 ? "bg-yellow-500/10 border-yellow-500/20"
                      : "bg-red-500/10 border-red-500/20"
                    }`}>
                      <div className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {quizResult.score}/{quizResult.total}
                      </div>
                      <p className="text-sm text-[#94A3B8]" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {quizResult.score === quizResult.total ? "Sempurna! Kamu benar-benar paham lesson ini."
                         : quizResult.score >= Math.ceil(quizResult.total / 2) ? "Bagus! Kamu sudah pahami sebagian besar konsep."
                         : "Perlu baca ulang. Coba lagi setelah review lesson."}
                      </p>
                    </div>
                    {quizResult.results.map((r, i) => (
                      <div key={i} data-testid={`quiz-result-${i}`}
                        className={`border rounded-xl p-4 ${r.is_correct ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {r.is_correct ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            : <div className="w-4 h-4 rounded-full border-2 border-red-400 flex items-center justify-center flex-shrink-0"><X className="w-2 h-2 text-red-400" /></div>}
                          <span className="text-xs font-mono text-[#94A3B8]">Soal {i+1}</span>
                        </div>
                        <p className="text-sm text-white mb-2 leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{r.q}</p>
                        <div className="space-y-1">
                          {r.options.map((opt, oi) => {
                            const letter = opt.charAt(0);
                            const isCorrect = letter === r.correct_answer;
                            const isUserWrong = letter === r.user_answer && !r.is_correct;
                            return (
                              <div key={oi} className={`text-xs px-3 py-1.5 rounded-lg ${
                                isCorrect ? "bg-emerald-500/15 text-emerald-300"
                                : isUserWrong ? "bg-red-500/15 text-red-300 opacity-70"
                                : "text-[#4a5568]"
                              }`} style={{ fontFamily: "'Inter', sans-serif" }}>{opt}</div>
                            );
                          })}
                        </div>
                        {r.explanation && (
                          <p className="text-xs text-[#94A3B8] mt-2.5 italic border-l-2 border-white/10 pl-3" style={{ fontFamily: "'IBM Plex Serif', serif" }}>{r.explanation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : quiz ? (
                  // Questions view
                  <div className="space-y-7">
                    {quiz.questions.map((q, qi) => (
                      <div key={qi}>
                        <p className="text-[10px] font-mono text-purple-400 mb-1.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Soal {qi+1} dari {quiz.questions.length}</p>
                        <p className="text-sm font-medium text-white mb-3 leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{q.q}</p>
                        <div className="space-y-2">
                          {q.options.map((opt, oi) => {
                            const letter = opt.charAt(0);
                            const isSelected = userAnswers[qi] === letter;
                            return (
                              <button key={oi} onClick={() => setUserAnswers(prev => ({ ...prev, [qi]: letter }))}
                                data-testid={`quiz-option-${qi}-${oi}`}
                                className={"w-full text-left text-xs px-3 py-2.5 rounded-xl border transition-colors duration-150 " + (
                                  isSelected ? "bg-indigo-500/20 border-indigo-500/30 text-white" : "border-white/10 text-[#94A3B8] hover:border-white/20 hover:text-white bg-white/3"
                                )} style={{ fontFamily: "'Inter', sans-serif" }}>
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* Quiz Footer */}
              {!quizLoading && (
                <div className="p-4 border-t border-white/10 flex-shrink-0">
                  {quizResult ? (
                    <div className="flex gap-3">
                      <button onClick={() => { setQuizResult(null); setUserAnswers({}); }}
                        className="flex-1 text-sm text-[#94A3B8] border border-white/10 hover:border-white/20 py-2.5 rounded-xl transition-colors duration-200"
                        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Ulangi Quiz</button>
                      <button onClick={closeQuiz}
                        className="flex-1 text-sm text-white bg-indigo-600 hover:bg-indigo-500 py-2.5 rounded-xl transition-colors duration-200"
                        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Selesai</button>
                    </div>
                  ) : quiz ? (
                    <button data-testid="submit-quiz-btn" onClick={submitQuiz}
                      disabled={Object.keys(userAnswers).length < (quiz.questions?.length || 3) || quizSubmitting}
                      className="w-full flex items-center justify-center gap-2 text-sm text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-40 py-2.5 rounded-xl transition-colors duration-200"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {quizSubmitting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Memeriksa...</>
                        : `Submit Jawaban (${Object.keys(userAnswers).length}/${quiz.questions?.length || 3} dijawab)`}
                    </button>
                  ) : null}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SectionBlock({ section }) {
  const baseText = { fontFamily: "'IBM Plex Serif', serif", lineHeight: "1.8" };
  switch (section.type) {
    case "section_title":
      return <h2 className="text-lg font-bold text-white mt-8 mb-3 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{section.text}</h2>;
    case "quote":
      return (
        <blockquote className="border-l-2 border-indigo-500 pl-5 py-2">
          <p className="text-base text-indigo-200 italic leading-relaxed" style={baseText}>{section.text}</p>
          {section.source && <cite className="text-xs text-[#94A3B8] not-italic mt-2 block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>&mdash; {section.source}</cite>}
        </blockquote>
      );
    case "example":
      return (
        <div className="bg-[#1A1A20] border border-white/10 rounded-xl p-5">
          <span className="text-[10px] font-mono text-purple-400 uppercase tracking-widest">Contoh Nyata</span>
          <p className="text-sm text-[#F8FAFC] leading-relaxed mt-2 whitespace-pre-line" style={baseText}>{section.text}</p>
        </div>
      );
    default:
      return <p className="text-base text-[#F8FAFC] leading-relaxed whitespace-pre-line" style={baseText}>{section.text}</p>;
  }
}

function IdeaCard({ idea, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.07 }}
      data-testid={`idea-card-${index}`} className="bg-[#1A1A20] border border-white/10 rounded-xl p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-white leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{idea.title}</h4>
        <div className="flex gap-1 flex-shrink-0">
          {(idea.platforms || []).map(p => <span key={p} className="text-[10px] text-purple-400 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded-full">{p}</span>)}
        </div>
      </div>
      {idea.angle && <p className="text-xs text-indigo-300 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{idea.angle}</p>}
      {idea.hook && <p className="text-xs text-[#94A3B8] italic leading-relaxed" style={{ fontFamily: "'IBM Plex Serif', serif" }}>{idea.hook}</p>}
    </motion.div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-[60vh]">
      <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );
}
